import { useEffect, useState } from 'react';
import { Button, Form, Image, InputGroup, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';

// Helper: konversi DD-MM-YYYY -> YYYY-MM-DD (untuk input date)
const convertToDateInput = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

// Helper: konversi YYYY-MM-DD -> DD-MM-YYYY (untuk API)
const convertToAPIFormat = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const PromoProductAddModal = ({
  show,
  handleClose,
  handleAddPromoProducts,
  parsingDataEdit,
  editPromoProduct,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    code_promo: "",
    discount: 0,
    type_promo: "",
    image: "",
    maxUser: "",
    desc: "",
    expired_date: "",
  });

  useEffect(() => {
    if (parsingDataEdit) {
      setFormData({
        name: parsingDataEdit.name || "",
        code_promo: parsingDataEdit.code_promo || "",
        discount: Number(parsingDataEdit.discount) || 0,
        image: parsingDataEdit.image || "",
        type_promo: parsingDataEdit.type_promo || "",
        maxUser: parsingDataEdit.maxUser || "",
        desc: parsingDataEdit.desc || "",
        expired_date: convertToDateInput(parsingDataEdit.expired_date) || "",
      });
    } else {
      setFormData({
        name: "",
        code_promo: "",
        discount: 0,
        type_promo: "",
        image: "",
        maxUser: "",
        desc: "",
        expired_date: "",
      });
    }
  }, [parsingDataEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "discount" ? Number(value) : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: reader.result,
        }));
      };
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validasi tambahan: gambar wajib diisi hanya saat tambah baru
    if (!parsingDataEdit && !formData.image) {
      Swal.fire({
        icon: 'error',
        title: 'Validasi Gagal',
        text: 'Mohon unggah gambar promo.',
      });
      return;
    }

    const payload = {
      name: formData.name,
      image: formData.image,
      code_promo: formData.code_promo,
      type_promo: formData.type_promo,
      desc: formData.desc,
      discount: formData.discount,
      maxUser: formData.maxUser,
      expired_date: convertToAPIFormat(formData.expired_date),
    };

    try {
      if (parsingDataEdit != null) {
        await editPromoProduct(parsingDataEdit.id, payload);
      } else {
        await handleAddPromoProducts(payload);
      }

      // Notifikasi sukses (toast)
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: `Promo berhasil ${parsingDataEdit ? 'diperbarui' : 'ditambahkan'}.`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      // Reset form dan tutup modal
      setFormData({
        name: "",
        code_promo: "",
        discount: null,
        type_promo: "",
        image: "",
        maxUser: "",
        desc: "",
        expired_date: "",
      });
      handleClose();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error?.message || 'Terjadi kesalahan saat menyimpan promo.',
      });
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Promo Product</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit}>
          {/* Name & Code Promo */}
          <Form.Group className="mb-3">
            <InputGroup>
              <Form.Label>Name & Code Promo</Form.Label>
            </InputGroup>
            <InputGroup>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Name Promo ..."
                required
              />
              <Form.Control
                type="text"
                name="code_promo"
                value={formData.code_promo}
                onChange={handleChange}
                placeholder="Code Promo ..."
                required
              />
            </InputGroup>
          </Form.Group>

          {/* Max User & Discount */}
          <Form.Group className="mb-3">
            <InputGroup>
              <Form.Label>Max user & Discount promo</Form.Label>
            </InputGroup>
            <InputGroup>
              <Form.Control
                type="number"
                name="maxUser"
                value={formData.maxUser}
                onChange={handleChange}
                placeholder="Max user Promo..."
                required
              />
              <Form.Control
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleChange}
                placeholder="Discount (%)..."
                required
              />
            </InputGroup>
          </Form.Group>

          {/* Deskripsi */}
          <Form.Group className="mb-3">
            <Form.Label>Deskripsi</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="desc"
              value={formData.desc}
              onChange={handleChange}
              placeholder="Deskripsi promo..."
              required
            />
          </Form.Group>

          {/* Expired Date */}
          <Form.Group className="mb-3">
            <Form.Label>Tanggal Expired (DD-MM-YYYY)</Form.Label>
            <Form.Control
              type="date"
              name="expired_date"
              value={formData.expired_date}
              onChange={handleChange}
              required
            />
          </Form.Group>

          {/* Type Promo */}
          <Form.Group className="mb-2" style={{ fontSize: 13 }}>
            <Form.Label>Type Promo</Form.Label>
            <Form.Select
              name="type_promo"
              value={formData.type_promo}
              onChange={handleChange}
              style={{ fontSize: 13 }}
              required
            >
              <option style={{ fontSize: 13 }} value="" disabled>
                -- Select a Type Promo --
              </option>
              <option style={{ fontSize: 13 }} value="1">
                Hospital
              </option>
              <option style={{ fontSize: 13 }} value="2">
                Product
              </option>
            </Form.Select>
          </Form.Group>

          {/* Upload Image */}
          <Form.Group className="mb-3">
            <Form.Label>Upload Image {!parsingDataEdit && <span className="text-danger">*</span>}</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required={!parsingDataEdit} // wajib hanya saat tambah baru
            />
          </Form.Group>

          {/* Image Preview */}
          {formData.image && (
            <div className="mb-3">
              <Image src={formData.image} alt="Preview" fluid rounded />
            </div>
          )}

          <Button variant="primary" type="submit">
            Submit
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default PromoProductAddModal;